'use client'

import type { PropsWithChildren, ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import { isEmpty } from 'lodash'
import { usePathname, useRouter } from '@/navigation'
import type { NavigationItem } from './models/mainLayoutModels'
import Title from '../../components/Title'
import { cx } from 'class-variance-authority'
import { resolvePath } from '@/shared/utils/routesUtils'
import Link from '@/shared/v2/shared/components/Link'
import type { Messages } from '@/types/global'
import ToggleGroup from '../../components/ToggleGroup'
import { useMe } from '@/shared/hooks/useMe'
import CompaniesSelect from '../../components/CompaniesSelect'

import CreateSubscriptionDialog from '@/app/[locale]/(dashboard)/(list)/(developers)/_components/CreateSubscriptionDialog'
import CreateOrUpdateMemberDrawer from '@/app/[locale]/(dashboard)/(settings)/components/CreateOrUpdateMemberDrawer'
import CreateApplicationDrawer from '@/app/[locale]/(dashboard)/(list)/(developers)/_components/CreateApplicationDrawer'
import PrivilegesButton from '../../components/PrivilegesButton'
import CopyCompanyIdButton from '../../components/CopyCompanyIdButton'
import DebtorBulkUploadDialog from '@/modules/bulk/components/DebtorBulkUploadDialog'
import NewImportDrawer from '@/modules/imports/components/NewImportDrawer'

export type PageLayoutProps = PropsWithChildren<{
    title: string
    tabs?: NavigationItem[]
}>

type ActionInfo =
    | {
          type: 'link'
          label: keyof Messages['root']['actions']
          href: string
      }
    | {
          type: 'button'
          label: ReactNode
      }

const resolveAction = (pathname: string): ActionInfo[] | null => {
    const actionMap: { [key: string]: ActionInfo[] } = {
        [resolvePath('/')]: [
            {
                type: 'link',
                label: 'createLoan',
                href: resolvePath('/loans/create')
            }
        ],
        [resolvePath('/users')]: [
            {
                type: 'link',
                label: 'createUser',
                href: resolvePath('/users/create')
            }
        ],
        [resolvePath('/credit-limits')]: [
            {
                type: 'link',
                label: 'createCreditLimit',
                href: resolvePath('/credit-limits/create')
            }
        ],
        [resolvePath('/debtors')]: [
            {
                type: 'button',
                label: <DebtorBulkUploadDialog />
            },
            {
                type: 'link',
                label: 'createDebtor',
                href: resolvePath('/debtors/create')
            }
        ],
        [resolvePath('/webhooks')]: [
            {
                type: 'button',
                label: <CopyCompanyIdButton />
            },
            {
                type: 'button',
                label: <CreateSubscriptionDialog />
            }
        ],
        [resolvePath('/members')]: [
            {
                type: 'button',
                label: <CreateOrUpdateMemberDrawer mode="create" member={null} />
            }
        ],
        [resolvePath('/authentication')]: [
            {
                type: 'button',
                label: <CopyCompanyIdButton />
            },
            {
                type: 'button',
                label: <CreateApplicationDrawer />
            }
        ],
        [resolvePath('/imports')]: [
            {
                type: 'button',
                label: <NewImportDrawer />
            }
        ],
        [resolvePath('/event-simulator')]: [
            {
                type: 'button',
                label: <CopyCompanyIdButton />
            }
        ]
    }

    return actionMap[pathname] || null
}

const PageLayout = ({ title, tabs, children }: PageLayoutProps) => {
    const pathname = usePathname()
    const router = useRouter()
    const t = useTranslations('root')
    const { isGod } = useMe()

    const tabsWithTranslatedLabels =
        tabs?.map((tab) => ({
            value: tab.key,
            label: t(tab.label)
        })) ?? []

    const onSwitchTab = (tab: string) => {
        router.push(tab)
    }

    const actions = resolveAction(pathname)

    return (
        <div className={cx(['grid grid-cols-1 pb-8', isEmpty(tabsWithTranslatedLabels) ? 'gap-6' : 'gap-8'])}>
            <div className="flex items-center justify-between h-[32px]">
                <Title.TwoXl data-testid="__topper-title__">{title}</Title.TwoXl>
                <div className="flex gap-2 flex-row">
                    {isGod ? <CompaniesSelect /> : null}
                    {actions && !isEmpty(actions) ? (
                        <div className="flex gap-2 flex-row">
                            {actions.map((action, index) =>
                                action.type === 'link' ? (
                                    <Link
                                        key={action.label}
                                        href={action.href}
                                        data-testid="__page-layout-link-action__"
                                        className="min-w-fit no-underline"
                                    >
                                        <PrivilegesButton>{t(`actions.${action.label}`)}</PrivilegesButton>
                                    </Link>
                                ) : (
                                    // eslint-disable-next-line react/no-array-index-key -- a display-only list: its rows hold no state, and their values can repeat
                                    <span key={index} data-testid="__page-layout-button-action__">
                                        {action.label}
                                    </span>
                                )
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
            {!isEmpty(tabsWithTranslatedLabels) ? (
                <div>
                    <ToggleGroup
                        defaultValue={pathname}
                        items={tabsWithTranslatedLabels}
                        onChange={(value) => onSwitchTab(value)}
                    />
                </div>
            ) : null}
            {children}
        </div>
    )
}

export default PageLayout
